/**
 * RiskAssessmentExecutionModal — execution view for one rolled-out risk
 * assessment program.
 *
 * Three-level nested tree:
 *   Risk row  →  Control row (mitigating)  →  Implementation row
 *   [L, S]       [Existence, Effectiveness]      [Pass / Fail / NA]
 *
 * Mode matrix:
 *   readOnly=true               → GRC admin / observer view. All inputs
 *                                  disabled, no Submit/Approve footer.
 *                                  Used by Risk Programs admin tab.
 *   readOnly=false, isChecker=true → Checker. Inputs disabled (maker did
 *                                    the data entry). Footer shows Approve.
 *   readOnly=false, isChecker=false → Maker. Inputs enabled. Footer shows
 *                                     Submit for Approval.
 *
 * Server is authoritative for residual/inherent; this modal only displays.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Send, Check, Eye, Plus, X } from 'lucide-react';
import {
    riskAssessmentProgramService,
    RiskAssessmentProgramDetail,
    ProgramRiskRow,
    ProgramControlScoreRow,
    ProgramImplScoreRow,
} from '../../services/riskAssessmentProgramService';
import {
    riskVulnerabilityService,
    RiskVulnerability,
    VulnerabilitySeverity,
    VULNERABILITY_SEVERITIES,
} from '../../services/riskVulnerabilityService';
import {
    controlDeficiencyService,
    ControlDeficiency,
} from '../../services/controlDeficiencyService';
import Modal from '../common/Modal';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import InlineIssueMeta from '../common/InlineIssueMeta';
import Button from '../common/Button';
import Badge from '../common/Badge';
import FormControl from '../common/FormControl';
import Toast, { ToastMessage } from '../common/Toast';
import extractApiError from '../../utils/apiError';
import { statusVariant } from '../../utils/statusVariant';
import { useFieldValidation } from '../../validation/useFieldValidation';
import {
    SeamlessSurface,
    SeamlessCard,
    AssessmentPanel,
    AssessRow,
    NetScore,
    SectionLabel,
    ImplList,
    ResponseBar,
    SeamlessSkeleton,
    scoreBand,
    riskBand,
} from '../audits/procedure-responses/seamless/SeamlessKit';

const residual = (r: ProgramRiskRow): string => {
    if (!r.likelihood || !r.severity) return '—';
    const ctrls = r.controls;
    if (ctrls.length === 0) return String(r.likelihood * r.severity);
    const avgs = ctrls
        .filter(c => c.existence_score && c.effectiveness_score)
        .map(c => ((c.existence_score! + c.effectiveness_score!) / 2));
    if (avgs.length === 0) return String(r.likelihood * r.severity);
    const ctrlAvg = avgs.reduce((s, x) => s + x, 0) / avgs.length;
    const score = Math.round(r.likelihood * r.severity * (6 - ctrlAvg) / 5);
    return String(Math.max(1, Math.min(25, score)));
};

interface RiskAssessmentExecutionModalProps {
    programId: number;
    isChecker?: boolean;
    readOnly?: boolean;
    onClose: () => void;
    onSaved?: () => void;
}

/** RemarksModal — per-row note editor as a popup (parity with the audit bodies). */
const RemarksModal: React.FC<{
    heading: string;
    title: string;
    initialValue: string;
    readOnly: boolean;
    placeholder?: string;
    testId?: string;
    onCommit: (value: string) => void;
    onClose: () => void;
}> = ({ heading, title, initialValue, readOnly, placeholder, testId, onCommit, onClose }) => {
    const [draft, setDraft] = useState(initialValue);
    return (
        <Modal isOpen onClose={onClose} size="md">
            <Modal.Header onClose={onClose}>{heading}</Modal.Header>
            <Modal.Body>
                <p className="response-remarks-question">{title}</p>
                <FormControl.Textarea
                    label=""
                    data-testid={testId}
                    value={draft}
                    onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setDraft(e.target.value)}
                    rows={5}
                    placeholder={placeholder}
                    autoFocus
                    disabled={readOnly}
                />
            </Modal.Body>
            <Modal.Footer>
                <Button variant="fill" onClick={() => { onCommit(draft); onClose(); }} disabled={readOnly}>Done</Button>
            </Modal.Footer>
        </Modal>
    );
};

const RiskAssessmentExecutionModal: React.FC<RiskAssessmentExecutionModalProps> = ({
    programId,
    isChecker = false,
    readOnly = false,
    onClose,
    onSaved,
}) => {
    const { formatDate } = useEntitySettings();
    const [detail, setDetail] = useState<RiskAssessmentProgramDetail | null>(null);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    // Cards are always open (no expand/collapse) — flat single-enclosure design.
    const [toast, setToast] = useState<ToastMessage | null>(null);
    // Vulnerabilities raised against each risk (keyed by rsk_register id).
    const [vulnsByRisk, setVulnsByRisk] = useState<Record<number, RiskVulnerability[]>>({});
    // Deficiencies raised against each control (keyed by ctr control id).
    const [defsByControl, setDefsByControl] = useState<Record<number, ControlDeficiency[]>>({});
    const [raiseFor, setRaiseFor] = useState<ProgramRiskRow | null>(null);
    // Checker reject flow — project Modal (not window.prompt); reason sent to maker.
    const [showReject, setShowReject] = useState(false);
    const [rejectReason, setRejectReason] = useState('');
    const [rejectErr, setRejectErr] = useState<string | null>(null);
    // Note / observation popup (parity with the audit bodies).
    const [remarks, setRemarks] = useState<
        { heading: string; title: string; value: string; placeholder?: string; testId?: string; onCommit: (v: string) => void } | null
    >(null);

    const writeBlocked = readOnly || isChecker;

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const d = await riskAssessmentProgramService.get(programId);
            setDetail(d);
            // Load vulnerabilities for each risk in scope (all sources, so prior
            // findings show too). Failures degrade to an empty list per risk.
            const vulnEntries = await Promise.all(
                d.risks.map(async r => {
                    try { return [r.risk_id, await riskVulnerabilityService.listForRisk(r.risk_id)] as const; }
                    catch { return [r.risk_id, [] as RiskVulnerability[]] as const; }
                }),
            );
            setVulnsByRisk(Object.fromEntries(vulnEntries));
            // Load deficiencies for each mitigating control in scope, so they show
            // inline on the control node exactly like vulnerabilities on the risk
            // node (audit parity). De-dup control ids — a control can mitigate
            // several risks but we only need one fetch per control.
            const controlIds = Array.from(
                new Set(d.risks.flatMap(r => r.controls.map(c => c.control_id))),
            );
            const defEntries = await Promise.all(
                controlIds.map(async cid => {
                    try { return [cid, await controlDeficiencyService.listForControl(cid)] as const; }
                    catch { return [cid, [] as ControlDeficiency[]] as const; }
                }),
            );
            setDefsByControl(Object.fromEntries(defEntries));
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to load program') });
        } finally {
            setLoading(false);
        }
    }, [programId]);
    useEffect(() => { load(); }, [load]);


    const patchRisk = (riskScoreId: number, patch: Partial<ProgramRiskRow>) => {
        setDetail(prev => prev ? {
            ...prev,
            risks: prev.risks.map(r => r.score_id === riskScoreId ? { ...r, ...patch } : r),
        } : prev);
    };
    const patchControl = (controlScoreId: number, patch: Partial<ProgramControlScoreRow>) => {
        setDetail(prev => prev ? {
            ...prev,
            risks: prev.risks.map(r => ({
                ...r,
                controls: r.controls.map(c => c.control_score_id === controlScoreId ? { ...c, ...patch } : c),
            })),
        } : prev);
    };
    const patchImpl = (implScoreId: number, patch: Partial<ProgramImplScoreRow>) => {
        setDetail(prev => prev ? {
            ...prev,
            risks: prev.risks.map(r => ({
                ...r,
                controls: r.controls.map(c => ({
                    ...c,
                    implementations: c.implementations.map(i => i.impl_score_id === implScoreId ? { ...i, ...patch } : i),
                })),
            })),
        } : prev);
    };

    const saveRiskSliders = async (r: ProgramRiskRow, patch: { likelihood?: number; severity?: number; remarks?: string }) => {
        if (writeBlocked) return;
        try { await riskAssessmentProgramService.updateRiskScore(programId, r.score_id, patch); }
        catch (e: any) { setToast({ type: 'error', message: extractApiError(e, 'Failed to save risk score') }); }
    };
    const saveControlSliders = async (c: ProgramControlScoreRow, patch: { existence_score?: number; effectiveness_score?: number; remarks?: string }) => {
        if (writeBlocked) return;
        try { await riskAssessmentProgramService.updateControlScore(programId, c.control_score_id, patch); }
        catch (e: any) { setToast({ type: 'error', message: extractApiError(e, 'Failed to save control score') }); }
    };
    const saveImpl = async (i: ProgramImplScoreRow, patch: { result?: 'Pass' | 'Fail' | 'NA'; remarks?: string }) => {
        if (writeBlocked) return;
        try { await riskAssessmentProgramService.updateImplScore(programId, i.impl_score_id, patch); }
        catch (e: any) { setToast({ type: 'error', message: extractApiError(e, 'Failed to save impl score') }); }
    };

    const submit = async () => {
        setBusy(true);
        try {
            await riskAssessmentProgramService.submit(programId);
            setToast({ type: 'success', message: 'Program submitted for approval.' });
            onSaved?.(); onClose();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to submit') });
        } finally { setBusy(false); }
    };
    const approve = async () => {
        setBusy(true);
        try {
            await riskAssessmentProgramService.approve(programId);
            setToast({ type: 'success', message: 'Program approved.' });
            onSaved?.(); onClose();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to approve') });
        } finally { setBusy(false); }
    };
    const reject = async () => {
        const reason = rejectReason.trim();
        if (!reason) { setRejectErr('A reason is required to reject.'); return; }
        setBusy(true);
        try {
            await riskAssessmentProgramService.reject(programId, reason);
            setToast({ type: 'success', message: 'Program rejected — returned to maker.' });
            setShowReject(false);
            onSaved?.(); onClose();
        } catch (e: any) {
            setToast({ type: 'error', message: extractApiError(e, 'Failed to reject') });
        } finally { setBusy(false); }
    };

    const allDone = (() => {
        if (!detail) return false;
        for (const r of detail.risks) {
            if (!r.likelihood || !r.severity) return false;
            for (const c of r.controls) {
                if (!c.existence_score || !c.effectiveness_score) return false;
                for (const i of c.implementations) {
                    if (!i.result) return false;
                }
            }
        }
        return detail.risks.length > 0;
    })();

    // Match the staff procedure header format: Procedure: "<Name>" (<Type>).
    const headerTitle = detail
        ? <>Procedure: "<span className="item-highlight">{detail.program_name}</span>" (Risk Assessment)</>
        : 'Loading…';

    return (
        <Modal isOpen onClose={onClose} size="xl" closeOnOverlayClick={false}>
            <Modal.Header>
                {headerTitle}
                {readOnly && (
                    <span className="status-badge sm neutral" style={{ marginLeft: 10 }} data-testid="BDG_risk_exec_readonly">
                        <Eye size={11} style={{ marginRight: 3 }} /> Read-only
                    </span>
                )}
            </Modal.Header>
            <Modal.Body>
                {/* ── Loading skeleton ── */}
                {loading && <SeamlessSkeleton />}

                {/* ── Loaded content ── */}
                {!loading && detail && (
                    <div className="collapse-section-stack">
                        {/* Info bar */}
                        {/* Staff-style context row (label: value), not chip pills. */}
                        <div className="response-shell-context">
                            {detail.template_code && (
                                <span className="response-shell-meta-item">
                                    <span className="response-shell-meta-label">Template: </span>
                                    <span className="response-shell-meta-value">{detail.template_code}</span>
                                </span>
                            )}
                            {detail.location_name && (
                                <span className="response-shell-meta-item">
                                    <span className="response-shell-meta-label">Location: </span>
                                    <span className="response-shell-meta-value">{detail.location_name}</span>
                                </span>
                            )}
                            {detail.maker_name && (
                                <span className="response-shell-meta-item">
                                    <span className="response-shell-meta-label">Maker: </span>
                                    <span className="response-shell-meta-value">{detail.maker_name}</span>
                                </span>
                            )}
                            {detail.checker_name && (
                                <span className="response-shell-meta-item">
                                    <span className="response-shell-meta-label">Checker: </span>
                                    <span className="response-shell-meta-value">{detail.checker_name}</span>
                                </span>
                            )}
                            {(detail.start_date || detail.end_date) && (
                                <span className="response-shell-meta-item">
                                    <span className="response-shell-meta-label">Period: </span>
                                    <span className="response-shell-meta-value">{formatDate(detail.start_date)} → {formatDate(detail.end_date)}</span>
                                </span>
                            )}
                            <Badge variant={statusVariant(detail.status)} size="sm">{detail.status}</Badge>
                        </div>

                        {/* Checker's reject note — shown to the maker after a bounce-back
                            so they know what to fix. Hidden once re-approved. */}
                        {!isChecker && detail.rejection_reason && detail.status !== 'Approved' && detail.status !== 'Closed' && (
                            <div className="response-inline-warning" style={{ margin: '0 0 12px', display: 'flex', gap: 8, alignItems: 'flex-start' }} data-testid="BANNER_rat_reject_note">
                                <X size={16} style={{ flexShrink: 0, marginTop: 2 }} />
                                <span><strong>Rejected by checker:</strong> {detail.rejection_reason}</span>
                            </div>
                        )}

                        {detail.risks.length === 0 && (
                            <p className="collapse-section__empty">No risks in scope.</p>
                        )}

                        <div className="seamless-tight">
                        <SeamlessSurface>
                            {detail.risks.map((r, idx) => {
                                const inherent = r.likelihood && r.severity ? r.likelihood * r.severity : null;
                                const resStr = residual(r);
                                const resNum = resStr === '—' ? null : Number(resStr);
                                const riskVariant = riskBand(resNum);
                                const vulns = vulnsByRisk[r.risk_id] || [];
                                return (
                                    <SeamlessCard
                                        key={r.score_id}
                                        sno={idx + 1}
                                        snoLabel={`R${idx + 1}`}
                                        variant="risk"
                                        title={r.risk_name ?? ''}
                                        sub={r.description ?? undefined}
                                        inlineRemark={
                                            <InlineIssueMeta
                                                label="Vulnerabilities"
                                                issues={vulns}
                                                remark={r.remarks}
                                                testIdPrefix={`CHIP_rat_vuln_${r.score_id}`}
                                            />
                                        }
                                        assessment={
                                            <AssessmentPanel
                                                verdict={<NetScore text={resStr === '—' ? '—' : `${resStr}/25`} variant={riskVariant} title={`Inherent ${inherent ?? '—'} → Residual ${resStr} (out of 25)`} emphasis />}
                                            >
                                                <AssessRow
                                                    label="Lik"
                                                    labelTitle="Likelihood"
                                                    tone="risk"
                                                    value={r.likelihood ?? ''}
                                                    disabled={writeBlocked}
                                                    onChange={(v) => {
                                                        const n = typeof v === 'number' ? v : 0;
                                                        patchRisk(r.score_id, { likelihood: n >= 1 ? n : undefined });
                                                        if (n >= 1) saveRiskSliders(r, { likelihood: n });
                                                    }}
                                                    testId={`SEL_rat_likelihood_${r.score_id}`}
                                                />
                                                <AssessRow
                                                    label="Sev"
                                                    labelTitle="Severity"
                                                    tone="risk"
                                                    value={r.severity ?? ''}
                                                    disabled={writeBlocked}
                                                    onChange={(v) => {
                                                        const n = typeof v === 'number' ? v : 0;
                                                        patchRisk(r.score_id, { severity: n >= 1 ? n : undefined });
                                                        if (n >= 1) saveRiskSliders(r, { severity: n });
                                                    }}
                                                    testId={`SEL_rat_severity_${r.score_id}`}
                                                />
                                            </AssessmentPanel>
                                        }
                                        inlineActions={
                                            <ResponseBar
                                                note={{
                                                    label: 'Risk remarks',
                                                    filled: !!r.remarks,
                                                    onClick: () => setRemarks({
                                                        heading: 'Risk remarks',
                                                        title: r.risk_name ?? 'Risk',
                                                        value: r.remarks || '',
                                                        placeholder: 'Risk remarks…',
                                                        testId: 'INP_grc_risk_rationale',
                                                        onCommit: (v) => { patchRisk(r.score_id, { remarks: v }); saveRiskSliders(r, { remarks: v }); },
                                                    }),
                                                }}
                                                findings={{
                                                    label:
                                                        vulns.length > 0
                                                            ? `${vulns.length} vulnerabilit${vulns.length === 1 ? 'y' : 'ies'} — view / raise`
                                                            : 'Raise a vulnerability',
                                                    // Badge suppressed (count:0) — the list now renders inline as
                                                    // text below the node (audit parity); the flag is raise-only.
                                                    count: 0,
                                                    tone: 'danger',
                                                    onClick: () => { if (!writeBlocked) setRaiseFor(r); },
                                                }}
                                            />
                                        }
                                        impl={
                                            <>
                                                {r.controls.length === 0 && (
                                                    <p className="seamless__impl-sub" style={{ padding: '8px 0' }}>No mitigating controls.</p>
                                                )}
                                                {r.controls.map((c, ci) => {
                                                    const net = c.existence_score != null && c.effectiveness_score != null
                                                        ? c.existence_score * c.effectiveness_score : null;
                                                    const cVariant = scoreBand(net);
                                                    const defs = defsByControl[c.control_id] || [];
                                                    return (
                                                        <SeamlessCard
                                                            key={c.control_score_id}
                                                            sno={ci + 1}
                                                            snoLabel={`C${ci + 1}`}
                                                            variant="control"
                                                            title={c.control_name ?? ''}
                                                            inlineRemark={
                                                                <InlineIssueMeta
                                                                    label="Deficiencies"
                                                                    issues={defs}
                                                                    remark={c.remarks}
                                                                    testIdPrefix={`CHIP_rat_deficiency_${c.control_score_id}`}
                                                                />
                                                            }
                                                            assessment={
                                                                <AssessmentPanel
                                                                    verdict={<NetScore text={net == null ? 'Not scored' : `${net}/25`} variant={cVariant} />}
                                                                >
                                                                    <AssessRow
                                                                        label="Ex"
                                                                        labelTitle="Existence"
                                                                        tone="control"
                                                                        value={c.existence_score ?? ''}
                                                                        disabled={writeBlocked}
                                                                        onChange={(v) => {
                                                                            const n = typeof v === 'number' ? v : 0;
                                                                            patchControl(c.control_score_id, { existence_score: n >= 1 ? n : undefined });
                                                                            if (n >= 1) saveControlSliders(c, { existence_score: n });
                                                                        }}
                                                                    />
                                                                    <AssessRow
                                                                        label="Ef"
                                                                        labelTitle="Effectiveness"
                                                                        tone="control"
                                                                        value={c.effectiveness_score ?? ''}
                                                                        disabled={writeBlocked}
                                                                        onChange={(v) => {
                                                                            const n = typeof v === 'number' ? v : 0;
                                                                            patchControl(c.control_score_id, { effectiveness_score: n >= 1 ? n : undefined });
                                                                            if (n >= 1) saveControlSliders(c, { effectiveness_score: n });
                                                                        }}
                                                                    />
                                                                </AssessmentPanel>
                                                            }
                                                            inlineActions={
                                                                <ResponseBar
                                                                    note={{
                                                                        label: 'Control remarks',
                                                                        filled: !!c.remarks,
                                                                        onClick: () => setRemarks({
                                                                            heading: 'Control remarks',
                                                                            title: c.control_name ?? 'Control',
                                                                            value: c.remarks || '',
                                                                            placeholder: 'Control remarks…',
                                                                            onCommit: (v) => { patchControl(c.control_score_id, { remarks: v }); saveControlSliders(c, { remarks: v }); },
                                                                        }),
                                                                    }}
                                                                />
                                                            }
                                                            impl={
                                                                c.implementations.length > 0 ? (
                                                                    <>
                                                                        <SectionLabel>Implementations</SectionLabel>
                                                                        <ImplList
                                                                            observationLabel="Observation:"
                                                                            items={c.implementations.map(i => ({
                                                                                id: i.impl_score_id,
                                                                                sno: i.sequence_number,
                                                                                title: i.target_label || '—',
                                                                                sub: i.target_subtitle ?? null,
                                                                                result: i.result || '',
                                                                                observations: i.remarks || '',
                                                                            }))}
                                                                            disabled={writeBlocked}
                                                                            onResult={(id, result) => {
                                                                                patchImpl(id, { result: result as 'Pass' | 'Fail' | 'NA' });
                                                                                const i = c.implementations.find(x => x.impl_score_id === id);
                                                                                if (i) saveImpl(i, { result: result as 'Pass' | 'Fail' | 'NA' });
                                                                            }}
                                                                            onObservations={(id, text) => patchImpl(id, { remarks: text })}
                                                                            onOpenObservation={(id) => {
                                                                                const i = c.implementations.find(x => x.impl_score_id === id);
                                                                                setRemarks({
                                                                                    heading: 'Observation',
                                                                                    title: i?.target_label || 'Implementation',
                                                                                    value: i?.remarks || '',
                                                                                    placeholder: 'Observation (optional)',
                                                                                    onCommit: (v) => { patchImpl(id, { remarks: v }); if (i) saveImpl(i, { remarks: v }); },
                                                                                });
                                                                            }}
                                                                        />
                                                                    </>
                                                                ) : undefined
                                                            }
                                                        />
                                                    );
                                                })}
                                            </>
                                        }
                                    />
                                );
                            })}
                        </SeamlessSurface>
                        </div>
                    </div>
                )}
                <Toast toast={toast} onClose={() => setToast(null)} />
            </Modal.Body>
            {raiseFor && (
                <RaiseVulnerabilityDialog
                    riskId={raiseFor.risk_id}
                    sourceRefId={programId}
                    onClose={() => setRaiseFor(null)}
                    onCreated={(v) => {
                        setVulnsByRisk(prev => ({ ...prev, [v.risk_id]: [v, ...(prev[v.risk_id] || [])] }));
                        setToast({ type: 'success', message: 'Vulnerability raised.' });
                        setRaiseFor(null);
                    }}
                />
            )}
            {remarks && (
                <RemarksModal
                    heading={remarks.heading}
                    title={remarks.title}
                    initialValue={remarks.value}
                    placeholder={remarks.placeholder}
                    testId={remarks.testId}
                    readOnly={writeBlocked}
                    onCommit={remarks.onCommit}
                    onClose={() => setRemarks(null)}
                />
            )}
            {!readOnly && (
                <Modal.Footer>
                    {!isChecker && (
                        <Button variant="fill" icon={Send} onClick={submit} disabled={busy || !allDone || detail?.status === 'Submitted' || detail?.status === 'Closed'}>
                            {busy ? 'Submitting…' : detail?.status === 'Submitted' || detail?.status === 'Closed' ? 'Submitted' : 'Submit for Approval'}
                        </Button>
                    )}
                    {isChecker && (
                        <Button variant="danger" icon={X} onClick={() => { setRejectReason(''); setRejectErr(null); setShowReject(true); }} disabled={busy || detail?.status !== 'Submitted'}>
                            {busy ? 'Rejecting…' : 'Reject'}
                        </Button>
                    )}
                    {isChecker && (
                        <Button variant="update" icon={Check} onClick={approve} disabled={busy || detail?.status !== 'Submitted'}>
                            {busy ? 'Approving…' : detail?.status === 'Closed' ? 'Approved' : 'Approve'}
                        </Button>
                    )}
                </Modal.Footer>
            )}

            {/* Checker reject-reason modal (replaces the native window.prompt). */}
            {showReject && (
                <Modal isOpen size="sm" onClose={() => setShowReject(false)}>
                    <Modal.Header onClose={() => setShowReject(false)}>Reject Assessment</Modal.Header>
                    <Modal.Body>
                        <p className="text-sm text-gray-500" style={{ marginBottom: 10 }}>
                            Send this assessment back to the maker. State why it is being rejected.
                        </p>
                        <FormControl.Textarea
                            rows={4}
                            value={rejectReason}
                            placeholder="Reason for rejection (required)"
                            error={rejectErr || undefined}
                            onChange={(e) => { setRejectReason(e.target.value); if (rejectErr) setRejectErr(null); }}
                            data-testid="INP_rat_program_reject_reason"
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="outline" onClick={() => setShowReject(false)} disabled={busy} data-testid="BTN_rat_program_reject_cancel">Cancel</Button>
                        <Button variant="danger" icon={X} onClick={reject} disabled={busy || !rejectReason.trim()} data-testid="BTN_rat_program_reject_confirm">
                            {busy ? 'Rejecting…' : 'Reject'}
                        </Button>
                    </Modal.Footer>
                </Modal>
            )}
        </Modal>
    );
};

interface RaiseVulnerabilityDialogProps {
    riskId: number;
    sourceRefId: number | null;
    onClose: () => void;
    onCreated: (v: RiskVulnerability) => void;
}

const RaiseVulnerabilityDialog: React.FC<RaiseVulnerabilityDialogProps> = ({ riskId, sourceRefId, onClose, onCreated }) => {
    const [description, setDescription] = useState('');
    const [severity, setSeverity] = useState<VulnerabilitySeverity>('Medium');
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const { errors, validateField, clearError, validateAll, reset } = useFieldValidation();

    useEffect(() => {
        setDescription('');
        setSeverity('Medium');
        setErr(null);
        reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [riskId]);

    const submit = async () => {
        const valid = validateAll([{ name: 'description', code: 110, value: description, required: true }]);
        if (!valid) return;
        if (!description.trim()) { setErr('Description is required'); return; }
        setSaving(true);
        try {
            const created = await riskVulnerabilityService.create(riskId, {
                description: description.trim(),
                source: 'assessment',
                source_ref_id: sourceRefId,
                detected_severity: severity,
            });
            onCreated(created);
        } catch (e: any) {
            setErr(extractApiError(e, 'Failed to raise vulnerability'));
        } finally {
            setSaving(false);
        }
    };

    return createPortal(
        <div className="fixed inset-0 z-[10050] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50" onClick={onClose} />
            <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col">
                <div className="p-4 border-b border-slate-100 flex justify-between items-center">
                    <h3 className="text-sm font-bold text-slate-800">Raise Vulnerability</h3>
                    <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="BTN_rat_vuln_close"><X size={18} /></button>
                </div>
                <div className="p-4 flex flex-col gap-3">
                    <label className="text-xs font-medium text-gray-600">Severity<span className="text-red-500 ml-0.5" aria-hidden="true">*</span></label>
                    <select
                        value={severity}
                        onChange={e => setSeverity(e.target.value as VulnerabilitySeverity)}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white"
                        data-testid="SEL_rat_vuln_severity"
                    >
                        {VULNERABILITY_SEVERITIES.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                    <label className="text-xs font-medium text-gray-600">Description<span className="text-red-500 ml-0.5" aria-hidden="true">*</span></label>
                    <textarea
                        rows={5}
                        value={description}
                        onChange={e => { setDescription(e.target.value); clearError('description'); }}
                        onBlur={e => validateField('description', 110, e.target.value, { required: true })}
                        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm"
                        data-testid="INP_rat_vuln_description"
                    />
                    {errors['description'] && <div className="text-xs text-red-600">{errors['description']}</div>}
                    {err && <div className="text-xs text-red-600">{err}</div>}
                </div>
                <div className="p-4 border-t border-slate-100 flex justify-end gap-3">
                    <button onClick={onClose} disabled={saving} className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm">Cancel</button>
                    <button
                        onClick={submit}
                        disabled={saving || !description.trim()}
                        className="px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 disabled:opacity-50"
                        data-testid="BTN_rat_vuln_submit"
                    >
                        <Plus size={16} /> {saving ? 'Saving…' : 'Raise Vulnerability'}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default RiskAssessmentExecutionModal;
